import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, CheckCircle2, XCircle, Image as ImageIcon, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function AdminManualPayments() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [previewSrc, setPreviewSrc] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/manual-payments?status_filter=${tab}`);
    setItems(data);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/admin/manual-payments?status_filter=${tab}`);
      if (!cancelled) setItems(data);
    })();
    return () => { cancelled = true; };
  }, [tab]);

  const approve = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/manual-payments/${id}/approve`, { admin_note: note }); toast.success("Approved & plan activated"); setOpenItem(null); setNote(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const reject = async (id) => {
    if (!reason.trim()) { toast.error("Please add a reason"); return; }
    setBusy(id);
    try { await api.post(`/admin/manual-payments/${id}/reject`, { reason, admin_note: note }); toast.success("Rejected — user notified"); setOpenItem(null); setReason(""); setNote(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="px-5 lg:px-10 py-8 max-w-6xl mx-auto" data-testid="admin-manual-payments">
      <h1 className="text-3xl font-bold text-brand-navy">Manual Payments</h1>
      <p className="text-sm text-brand-muted mt-1">Review UPI / bank transfer requests submitted by users. Plans activate only after you approve.</p>

      <div className="mt-6 inline-flex bg-white border border-brand-borderc rounded-full p-1">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-1.5 rounded-full text-sm font-medium ${tab === t.id ? "bg-gradient-primary text-white" : "text-brand-text"}`}>{t.label}</button>
        ))}
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-brand-borderc divide-y divide-brand-borderc">
        {items.length === 0 ? (
          <div className="text-center py-12 text-brand-muted">No {tab} requests.</div>
        ) : items.map((r) => {
          const Icon = r.status === "approved" ? CheckCircle2 : r.status === "rejected" ? XCircle : Clock;
          const color = r.status === "approved" ? "text-emerald-600" : r.status === "rejected" ? "text-red-500" : "text-amber-600";
          return (
            <div key={r.id} className="px-5 py-4 grid sm:grid-cols-[1fr_auto] gap-3 items-start" data-testid={`row-${r.order_ref}`}>
              <div className="text-sm space-y-1">
                <div className="font-semibold text-brand-navy">{r.user_name} · {r.user_email}</div>
                <div className="text-brand-text">{r.plan.toUpperCase()} · {r.billing_cycle} · {r.method.toUpperCase()} · ₹{r.amount}</div>
                <div className="text-xs text-brand-muted">Ref <span className="font-mono">{r.order_ref}</span> · UTR <span className="font-mono">{r.utr}</span> · Payer {r.payer_name}{r.payer_reference ? ` · ${r.payer_reference}` : ""}</div>
                <div className="text-xs text-brand-muted">Submitted {new Date(r.created_at).toLocaleString()}{r.coupon ? ` · Coupon ${r.coupon}` : ""}</div>
                {r.notes && <div className="text-xs text-brand-text bg-brand-surface px-2 py-1 rounded mt-1">User note: {r.notes}</div>}
                {r.reject_reason && <div className="text-xs text-red-600">Reject reason: {r.reject_reason}</div>}
              </div>
              <div className="flex flex-col gap-2 items-end">
                <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest ${color}`}><Icon className="w-4 h-4" />{r.status}</div>
                {r.screenshot_url && (
                  <button onClick={() => setPreviewSrc(r.screenshot_url)} className="text-xs inline-flex items-center gap-1 text-brand-purple hover:underline"><ImageIcon className="w-3.5 h-3.5" />View screenshot</button>
                )}
                {r.status === "pending" && (
                  <button data-testid={`act-${r.order_ref}`} onClick={() => { setOpenItem(r); setReason(""); setNote(""); }} className="btn-primary-gradient rounded-full px-4 py-1.5 text-xs font-semibold">Review</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openItem && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={() => setOpenItem(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-purple font-semibold">Review payment</div>
              <div className="font-bold text-brand-navy mt-1">{openItem.user_email} · ₹{openItem.amount} · {openItem.plan.toUpperCase()}</div>
              <div className="text-xs text-brand-muted">UTR <span className="font-mono">{openItem.utr}</span> · {openItem.method.toUpperCase()}</div>
            </div>
            <div>
              <label className="text-xs text-brand-muted">Admin note (internal)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-xl border border-brand-borderc text-sm" />
            </div>
            <div>
              <label className="text-xs text-brand-muted">Reject reason (shared with user, only if rejecting)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 rounded-xl border border-brand-borderc text-sm" placeholder="e.g. UTR not found in our account…" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="reject-btn" disabled={busy === openItem.id} onClick={() => reject(openItem.id)} className="rounded-full px-5 py-2.5 text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50">Reject</button>
              <button data-testid="approve-btn" disabled={busy === openItem.id} onClick={() => approve(openItem.id)} className="rounded-full px-5 py-2.5 text-sm font-semibold btn-primary-gradient">Approve & activate</button>
            </div>
          </div>
        </div>
      )}

      {previewSrc && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPreviewSrc(null)}>
          <img src={previewSrc} alt="screenshot" className="max-h-[85vh] max-w-[90vw] rounded-xl" />
        </div>
      )}
    </div>
  );
}
